<template>
  <div id="accordionTourContacts" class="accordion">
    <div class="case-details accordion-item">
      <h2 id="headingTourContacts" class="accordion-header">
        <button
          aria-controls="collapseTourContacts"
          aria-expanded="true"
          class="accordion-button collapsed"
          data-bs-target="#collapseTourContacts"
          data-bs-toggle="collapse"
          type="button"
        >
          <div class="mb-0 d-flex align-items-center">
            <div class="bg-primary rounded-xs me-8 d-flex align-items-center justify-content-center" style="width: 24px; height: 24px; flex: none;">
              <v-icon icon="contact" size="xs" class="text-white" />
            </div>

            <div>
              <div class="h6 ff-secondary mb-0 fw-bold">{{ $t("clientTour.contact.contacts") }}</div>
              <div class="fw-light accordion-subtitle text-xs" style="font-size: 10px !important;">{{ $t("clientTour.contact.contactsSubtitle") }}</div>
            </div>
          </div>
        </button>
      </h2>

      <div
        id="collapseTourContacts"
        aria-labelledby="headingTourContacts"
        class="accordion-collapse collapse show"
        data-bs-parent="#accordionTourContacts"
      >
        <div class="accordion-body pb-16 pb-xl-24">
          <form id="case-tour-update-contact-text-form" class="d-flex flex-column justify-content-between h-100">
            <fieldset>
              <legend class="visually-hidden">{{ $t("clientTour.contact.contactInformation") }}</legend>

              <div class="form-outline">
                <label class="label text-xs fw-bold text-uppercase text-muted mb-2">
                  <span>{{ $t("clientTour.contact.contactInformation") }}</span>
                </label>

                <v-html-editor
                  id="case-tour-update-contact-text"
                  ref="case-tour-update-contact-text-ref"
                  :value="contactInformationLocal"
                  @input="contactInformationLocal = $event"
                  @media-uploaded="editorMedia.push($event)"
                  height="300"
                />
              </div>
            </fieldset>

            <v-button
              :disabled="!meta.valid || disableButton || !meta.dirty"
              :is-loading="accepted"
              class="btn-icon w-fit align-self-end mt-12"
              size="xs"
              variant="primary"
              @click="updateCase"
            >
              {{ $t("general.button.save") }}
              <v-icon icon="arrow-right" size="xs" space="ms-8" />
            </v-button>
          </form>
        </div>
      </div>
    </div>
  </div>
</template>

<script lang="ts" setup>
import { useI18n } from "vue-i18n";
import * as yup from "yup";
import { useField } from "vee-validate";
import VIcon from "@/components/vendor/basic/icon/VIcon.vue";
import Tour from "@/api/objects/Tour";
import VHtmlEditor from "@/components/vendor/htmlEditor/VHtmlEditor.vue";
import api from "@/api";
import useFormValidation from "@/helpers/form";
import VButton from "@/components/vendor/basic/button/VButton.vue";
import { ref } from "vue";
import { useToast } from "vue-toastification";

const props = defineProps<{
  agency: string;
  case: Tour;
  caseId: number;
}>();

const emit = defineEmits<{
  (e: "reloadCase"): void;
}>();

const { t } = useI18n();
const toast = useToast();
const disableButton = ref(false);
const editorMedia = ref<any[]>([]);

const rules = yup.object({
  contactInformationLocal: yup.string().nullable().label(t("clientTour.contact.contactInformation")),
});

const initialValues = {
  contactInformationLocal: props.case.contact_text,
};

const form = useFormValidation(rules, initialValues);
const { accepted, toggleAccepted, meta } = form;

const { value: contactInformationLocal } = useField<string>("contactInformationLocal");

const updateCase = async () => {
  try {
    toggleAccepted();
    disableButton.value = true;

    const data = {
      contact_text: contactInformationLocal.value,
    };

    await api.case.updateContactInfo(props.agency, props.caseId, data);

    if (editorMedia.value?.length > 0) {
      const editorPayload = {
        media: editorMedia.value,
        model_type: "tour",
        collection: "tinymce",
        model_id: props.case.id,
      };

      await api.media.confirm("s3", editorPayload);
    }

    toast.success(t("general.shared.savedChanges"));
    emit("reloadCase");
  } catch (e: any) {
    console.error(e);
    toast.error(e?.response?.data?.message || "Error occurred");
  } finally {
    disableButton.value = false;
    toggleAccepted();
  }
};
</script>

<style lang="scss" scoped>
.accordion-button,
.accordion-button:not(.collapsed) {
  background-color: transparent;
  padding: 0.5rem 1rem !important;
}

:deep(.form-control),
:deep(.vs__selected),
:deep(.vs__dropdown-option) {
  font-size: 0.8rem !important;
}

:deep(.v-form-label),
:deep(.form-check-label) {
  font-size: 0.75rem !important;
}

:deep(.v-form-input),
:deep(.v-form) {
  margin-bottom: 0.25rem !important;
}
</style>
